/**
 * @module parseFileData
 *
 * Unified file parser for CSV and Excel files.
 * Replaces duplicated parseFileData implementations in blotter and CRE.
 *
 * This is the **low-level** parsing utility. It reads a browser `File` object and
 * returns an array of row objects keyed by the raw header strings found in the file.
 * It does NOT normalize headers, apply transforms, validate, or merge -- those
 * concerns are handled by the hook layer (`useFileUploadProcessor` / `useUploadConfig`).
 *
 * **When to use this directly vs. the hook-based approach:**
 * - Use `parseFileData` when you need raw parsed data and will handle column
 *   normalization, transforms, and merge yourself (e.g., inside a custom
 *   `handleUpload` callback that predates the 1823-core upload pipeline).
 * - Use `useUploadConfig` (preferred) or `useFileUploadProcessor` when you want
 *   the full pipeline: parse -> normalize headers -> transform values -> validate
 *   -> merge with existing grid data.
 *
 * NOTE: Requires the `xlsx` package as a peer dependency.
 * Install it in your app: `npm install xlsx`
 *
 * @example <caption>Basic CSV parsing</caption>
 * import { parseFileData } from '@1823-partners/core';
 *
 * const handleUpload = async (file) => {
 *   // file is a browser File object from <input type="file"> or PamGrid's onUpload
 *   const rows = await parseFileData(file);
 *   // rows = [{ CUSIP: '14913UAN0', BOOK_YIELD: '3.5', ... }, ...]
 *   console.log(`Parsed ${rows.length} rows`);
 * };
 *
 * @example <caption>Excel parsing with options</caption>
 * import { parseFileData } from '@1823-partners/core';
 *
 * const rows = await parseFileData(excelFile, {
 *   cellDates: true,       // Parse date cells as JS Date objects (default: true)
 *   filterEmptyRows: true, // Skip fully-blank rows (default: true)
 *   preserveFalsy: true,   // Keep 0 and false instead of coercing to '' (default: true)
 * });
 *
 * @example <caption>Disabling preserveFalsy (legacy behavior)</caption>
 * // With preserveFalsy=false, cell value 0 becomes '' and false becomes ''
 * // This matches older blotter behavior but loses numeric zeros from Excel.
 * const rows = await parseFileData(file, { preserveFalsy: false });
 */

/**
 * Helper to get XLSX library via dynamic import.
 * @returns {Promise<Object>} XLSX library
 * @throws {Error} If xlsx is not installed as a peer dependency
 * @private
 */
const getXLSX = async () => {
  try {
    return await import('xlsx');
  } catch {
    throw new Error('xlsx library not available. Please install it: npm install xlsx');
  }
};

/**
 * Wrap FileReader in a Promise for async/await usage.
 *
 * @param {File} file - Browser File object (from `<input type="file">` or drag-and-drop)
 * @param {'arraybuffer'|'text'} readAs - How to read the file:
 *   - `'arraybuffer'` for Excel (.xlsx/.xls) files
 *   - `'text'` for CSV files
 * @returns {Promise<ArrayBuffer|string>} File contents
 * @throws {Error} If the FileReader encounters an error
 * @private
 */
const readFile = (file, readAs) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Error reading file'));
    if (readAs === 'arraybuffer') {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
};

/**
 * Convert a 2D array (headers + rows) into an array of row objects.
 *
 * Row 0 is treated as the header row. Each subsequent row becomes an object
 * whose keys are the header values and whose values are the corresponding cells.
 *
 * @param {Array<Array<*>>} rawData - 2D array where row 0 is headers.
 *   Produced by `XLSX.utils.sheet_to_json(sheet, { header: 1 })`.
 * @param {Object} options
 * @param {boolean} options.filterEmptyRows - When true, rows where every cell is
 *   `null`, `undefined`, or `''` are skipped. This handles trailing blank rows
 *   that Excel often includes.
 * @param {boolean} options.preserveFalsy - When true, uses `??` (nullish coalescing)
 *   instead of `||` for cell values. This means numeric `0`, boolean `false`, and
 *   empty string `''` are preserved as-is rather than being replaced with `''`.
 *   **Set to true (default) to avoid losing Excel 0 values.**
 * @returns {Array<Object>} Array of row objects keyed by header names.
 *   Headers that are falsy (empty string, null) are skipped -- their cells are not
 *   included in the row objects.
 * @private
 */
const convertToObjects = (rawData, { filterEmptyRows, preserveFalsy }) => {
  if (rawData.length === 0) {
    return [];
  }

  const headers = rawData[0] || [];
  const dataRows = rawData.slice(1);

  const result = [];
  for (const row of dataRows) {
    if (filterEmptyRows) {
      const isEmpty = row.every((cell) => cell === null || cell === undefined || cell === '');
      if (isEmpty) continue;
    }

    const obj = {};
    headers.forEach((header, i) => {
      if (header) {
        const cell = row[i];
        obj[header] = preserveFalsy ? (cell ?? '') : (cell || '');
      }
    });
    result.push(obj);
  }

  return result;
};

/**
 * Parse a CSV or Excel file into an array of row objects with header keys.
 *
 * Reads the first sheet of the workbook. Determines the file type from the
 * extension (`.csv`, `.xlsx`, `.xls`). Returns row objects keyed by the raw
 * header strings found in the file (no normalization applied).
 *
 * @param {File} file - Browser File object. Must have a `.name` property with
 *   a `.csv`, `.xlsx`, or `.xls` extension.
 * @param {Object} [options] - Parsing options (all optional with sensible defaults).
 * @param {boolean} [options.cellDates=true] - When true, Excel date cells are parsed
 *   as JavaScript `Date` objects. When false, they remain as Excel serial numbers.
 *   Only affects `.xlsx`/`.xls` files; CSV dates are always strings.
 * @param {boolean} [options.filterEmptyRows=true] - When true, rows where every cell
 *   is null/undefined/empty-string are excluded from the result. Recommended to
 *   keep true -- Excel files often have trailing blank rows.
 * @param {boolean} [options.preserveFalsy=true] - When true, numeric `0` and boolean
 *   `false` cell values are preserved. When false, they are coerced to `''`.
 *   **Why default is true:** Excel spreadsheets frequently contain `0` values
 *   (e.g., zero book yield, zero face amount) that must survive parsing. The old
 *   `||` pattern would silently convert `0` to `''`, causing data loss.
 * @returns {Promise<Array<Object>>} Array of row objects. Keys are the raw header
 *   strings from the file's first row. Values are the cell contents (strings,
 *   numbers, Date objects, or `''` for empty cells).
 * @throws {Error} If the file extension is not `.csv`, `.xlsx`, or `.xls`.
 * @throws {Error} If the `xlsx` peer dependency is not installed.
 *
 * @example <caption>Typical usage in a component</caption>
 * import { parseFileData } from '@1823-partners/core';
 *
 * const handleFileUpload = async (file) => {
 *   try {
 *     const rows = await parseFileData(file);
 *     // rows[0] might be: { CUSIP: '14913UAN0', 'Loan Name': 'Main Street', YIELD: 3.5 }
 *     setRowData(rows);
 *   } catch (err) {
 *     showNotification(err.message, 'error');
 *   }
 * };
 */
export const parseFileData = async (file, options = {}) => {
  const { cellDates = true, filterEmptyRows = true, preserveFalsy = true } = options;

  const XLSX = await getXLSX();
  const fileExtension = file.name.split('.').pop().toLowerCase();

  let rawData;

  if (fileExtension === 'csv') {
    const text = await readFile(file, 'text');
    const workbook = XLSX.read(text, { type: 'string' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    rawData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    const buffer = await readFile(file, 'arraybuffer');
    const workbook = XLSX.read(buffer, { type: 'array', cellDates });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    rawData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  } else {
    throw new Error('Unsupported file format. Please use CSV or Excel files.');
  }

  return convertToObjects(rawData, { filterEmptyRows, preserveFalsy });
};

export default parseFileData;
